/**
 * @hotelb2b/cookie-consent-core
 *
 * Vanilla TypeScript cookie consent state machine with no React or framework dependencies.
 * Used by React (apps/frontend) and Astro (apps/landing-pages) wrappers.
 *
 * See ADR-074 for architecture rationale.
 */

// Public API surface (Wave 4 L6 — dead exports removed).
//
// Only the factory + the types actually referenced by consumers
// (`apps/frontend/src/services/cookieService.ts`,
//  `apps/landing-pages/kiedynabory.pl/src/scripts/cookie-banner.ts`) are
// re-exported here. The internal building blocks (`state-machine`,
// `api-client`, `storage`, `gtm-consent`, `script-injector`) used to be
// re-exported "just in case" but no app imported them — tests import them
// directly from internal paths under `../`, and exposing them as a public
// surface was both dead code and a leaky abstraction. If you ever need a
// building block from outside this package, re-add the export explicitly
// and document the consumer.
export type {
  ConsentSnapshot,
  CookieConsentOptions,
  CookieConsentApi,
} from './types';

import { createStateMachine } from './state-machine';
import { createApiClient } from './api-client';
import { createBrowserStorage, createLocalStorageAdapter } from './storage';
import { updateGTMConsent } from './gtm-consent';
import type { CookieConsentOptions, CookieConsentApi, ConsentSnapshot } from './types';

/**
 * Factory function — creates the full cookie consent API.
 * This is the primary entry point for all consumers (React wrapper, Astro vanilla script).
 *
 * @example
 * // React
 * const consent = createCookieConsent({
 *   apiBase: 'https://api.kiedynabory.pl',
 *   brand: 'kiedynabory',
 *   policyVersion: LEGAL_VERSIONS.PRIVACY,
 *   bannerVersion: '2026-05-20',
 *   onConsentChange: (snap) => { if (snap?.categories.analytics) injectClarity(); }
 * });
 *
 * // Astro vanilla
 * const consent = createCookieConsent({ apiBase: window.__API_BASE__, brand: 'kiedynabory', ... });
 */
export function createCookieConsent(options: CookieConsentOptions): CookieConsentApi {
  const storage = options.storage ?? createBrowserStorage();
  const adapter = createLocalStorageAdapter(storage);
  const sm = createStateMachine({
    policyVersion: options.policyVersion,
    bannerVersion: options.bannerVersion,
    sessionId: generateSessionId(),
  });

  const apiClient = createApiClient({
    apiBase: options.apiBase,
    brand: options.brand,
    storage,
    fetchImpl: options.fetchImpl,
  });

  // Load persisted consent
  let current: ConsentSnapshot | null = adapter.loadConsent();
  if (current && sm.isExpiredSnapshot(current)) {
    adapter.clearConsent();
    current = null;
  }

  const listeners = new Set<(snapshot: ConsentSnapshot | null) => void>();

  function notifyListeners(snapshot: ConsentSnapshot | null): void {
    listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch {
        // Swallow listener errors to prevent one bad listener from blocking others
      }
    });
    options.onConsentChange?.(snapshot);
  }

  async function saveAndNotify(snapshot: ConsentSnapshot, isUpdate: boolean): Promise<void> {
    adapter.saveConsent(snapshot);
    current = snapshot;

    // Push Consent Mode v2 update to GTM (all 4 signals) — CCF-6.
    // Done synchronously BEFORE network call so consent state is propagated immediately
    // even when the API request fails.
    updateGTMConsent(snapshot);

    notifyListeners(snapshot);

    if (isUpdate) {
      await apiClient.putConsent(snapshot.sessionId, snapshot);
    } else {
      await apiClient.postConsent(snapshot);
    }

    // Flush any queued retries from previous failures
    await apiClient.flushRetryQueue();
  }

  const isUpdate = current !== null;

  return {
    async acceptAll(): Promise<void> {
      const snapshot = sm.acceptAll();
      await saveAndNotify(snapshot, isUpdate);
    },

    async essentialOnly(): Promise<void> {
      const snapshot = sm.essentialOnly();
      await saveAndNotify(snapshot, isUpdate);
    },

    async setCustom(categories: { analytics: boolean; marketing: boolean }): Promise<void> {
      const snapshot = sm.setCustom(categories);
      await saveAndNotify(snapshot, isUpdate);
    },

    getCurrent(): ConsentSnapshot | null {
      return current;
    },

    isExpired(): boolean {
      if (!current) return true;
      return sm.isExpiredSnapshot(current);
    },

    subscribe(listener: (snapshot: ConsentSnapshot | null) => void): () => void {
      listeners.add(listener);
      // Immediately emit current state
      listener(current);
      return () => listeners.delete(listener);
    },
  };
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (Safari <15.4)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
